import { useCallback, useEffect, useRef, useState } from 'react';
import './MobileSidebar.scss';
import Drawer from '@mui/material/Drawer';
import { useAppSelector } from '../../redux/app/hooks';
import { useDispatch } from 'react-redux';
import { logout, setIsSidebarOpen, setSplashvisible, setSelectedTabindex, setRefetchNotification } from '../../redux/slices/authSlice';
import images from '../../assets/images';
import Tooltip from '@mui/material/Tooltip';
import ConfirmationPopup from '../ConfirmationPopup/ConfirmationPopup';
import { useLocation, useNavigate } from 'react-router-dom';
import { FACILITATOR, PARTICIPANT, PTA_ROLE, SCHOOL_ADMIN, STUDENT_ROLE, TEACHER_ROLE, VIRTUAL } from '../../utils/constants';
import { getScreenDimensions } from '../../utils/helper';
import { useLazyQuery } from '@apollo/client';
import { GET_NOTIFICATIONS } from '../../pages/TeacherStudentClassroom/api/TeacherStudentClassroomQuery';

interface notificationType{
    Announcement:number;
};

const drawerStyle = {
    '& .MuiPaper-root': {
        width: getScreenDimensions()?.width > 375 ? 'calc(300px - 48px)' : 'calc(250px - 48px)',
        height: 'calc(100% - 100px)',
        padding: '60px 24px 40px 24px',
        overflowY: 'auto'
    }
};


const MobileSidebar = () => {

    const { adminProfileImage: profileImage, isSidebarOpen, role, adminProfile: profile, loginpage } = useAppSelector((state) => state?.auth);
    const { logoutGif, logoutIconMobile, classroomActiveIcon, classroomInactiveIcon, announcementActiveIcon, announcementInactiveIcon, settings_inactive, settings_active } = images;
    const dispatch = useDispatch();
    const schoolNameRef = useRef<HTMLDivElement | null>(null);
    const [isOverflow, setIsOverflow] = useState(false);
    const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();
    const StudentName = profile?.getCurrentUser?.nodes?.[0]?.uFullName;
    const classRoomName = useAppSelector((state) => state.auth.selectedClassRoomName);
    const tab = localStorage?.getItem('teacherStudentTabIndex');
    const [activeTab,setActiveTab] = useState(tab ? parseInt(tab) : 0);
    const isMylibaryActive = useAppSelector((state) => state.indication.indication.myLibary)
    const isLiveActive = useAppSelector((state) => state.indication.indication.live)
    const [notification, setNotification] = useState<notificationType>();
    const refetchNotification = useAppSelector((state) => state?.auth?.refetchNotification);

    const menuItems = [
        { id: 'Classroom', label: "Classroom", inactiveIcon: classroomInactiveIcon, activeIcon: classroomActiveIcon, link: role === STUDENT_ROLE ? "/student-classroom" : "/teacher-classroom" , set: "setOne",hasSubmenu: true, submenus : [
            { id: 'LiveClass', label: "Live Class" , index: 0},
            { id: 'MyLibrary', label: "My Library" , index: 1}
        ]},
        { id: 'Announcement', label: "Announcement", inactiveIcon: announcementInactiveIcon, activeIcon: announcementActiveIcon, link: role === STUDENT_ROLE ? "/student-announcement" : "/teacher-announcement" , set: "setOne"},
        { id: 'Settings', label: "Settings", inactiveIcon: settings_inactive, activeIcon: settings_active, link: (role === TEACHER_ROLE ? "/teacher-settings" : "/student-settings"), set: "setTwo" },
        { id: 'Logout', label: "Logout", inactiveIcon: logoutIconMobile, activeIcon: logoutIconMobile, link: "/logout", set: "setThree" },
    ];

    const toggleSidebarr = (newOpen: boolean) => () => {
        dispatch(setIsSidebarOpen({ isSidebarOpen: newOpen }));
    };

    const checkOverflow = () => {
        if (schoolNameRef.current) {
          setIsOverflow(schoolNameRef.current.scrollWidth > schoolNameRef.current.clientWidth);
        }
    };

    const onYesButtonClick = () => {
        if (role && [FACILITATOR, PARTICIPANT, SCHOOL_ADMIN].includes(role)) {
            navigate((role === FACILITATOR || ((role === SCHOOL_ADMIN && loginpage === FACILITATOR))) ? '/facilitator-login' : role === PARTICIPANT ? '/participant-login' : '/');
        }
        dispatch(logout());
        dispatch(setSplashvisible());
        setOpenConfirmationPopup(false);
    };

    const onNoButtonClick = () => {
        setOpenConfirmationPopup(false);
    };

    const onLogoutClick = () => {
        setOpenConfirmationPopup(true);
    };

    const getMenuItemsBySet = (set: string) => {
        return menuItems.filter(item => item.set === set);
    };

    const [getNotifications] = useLazyQuery(GET_NOTIFICATIONS, {
        fetchPolicy: "no-cache",
        onCompleted: (data) => {
            setNotification(data?.notifyUser);
        },
    });

    useEffect(() => {
        if (role !== PTA_ROLE && role !== PARTICIPANT) {
            getNotifications();
        }
    }, []);

    useEffect(() => {
        if (role !== PTA_ROLE && refetchNotification && role !== PARTICIPANT) {
            getNotifications();
            dispatch(setRefetchNotification({ refetchNotification: false }));
        }
    }, [refetchNotification]);

    const onMenuClick = async (menu: any) => {
        if (role !== PTA_ROLE && role !== PARTICIPANT) {
            await getNotifications();
        }
        dispatch(setIsSidebarOpen({ isSidebarOpen: false }));
        navigate(menu.link);
    };

    const handleSubmenuClick = (submenu:any) => {
        console.log('submenu selected', submenu)
        setActiveTab(submenu?.index);
        localStorage.setItem('teacherStudentTabIndex', String(submenu?.index));
        dispatch(setSelectedTabindex({selectedTabindex: String(submenu?.index)}));
        dispatch(setIsSidebarOpen({ isSidebarOpen: false }));
        navigate(role === STUDENT_ROLE ? '/student-classroom' : '/teacher-classroom');
    }
    

    const textRef = useRef<HTMLDivElement>(null);
  const [isEllipsisActive, setIsEllipsisActive] = useState(false);

  const checkEllipsis = useCallback(() => {
    if (textRef.current) {
      setIsEllipsisActive(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkEllipsis();
    window.addEventListener("resize", checkEllipsis);
    return () => window.removeEventListener("resize", checkEllipsis);
  }, [checkEllipsis, StudentName]);

    return (
        <Drawer open={isSidebarOpen} onClose={toggleSidebarr(false)} sx={drawerStyle}>
            <div className='sidebarContainer'>
                <div className='sidebarTopContainer'>
                    <div className='profileContainer'>
                        <div className='profileImageContainer'>
                            {profileImage && <img src={profileImage} className='profile' alt='profile' />}
                        </div>
                        <div className='nameContainer'>
                            <Tooltip
                                title={`${profile?.getCurrentUser?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saName ? profile?.getCurrentUser?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saName : "Katon Virtual School"}`}
                                disableHoverListener={!isOverflow}
                                placement="top"
                            >
                                <div className="name" ref={schoolNameRef} onMouseEnter={checkOverflow}>
                                    {`${profile?.getCurrentUser?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saName ? profile?.getCurrentUser?.nodes?.[0]?.schoolAdminsByUId?.nodes?.[0]?.saName : "Katon Virtual School"}`}
                                </div>
                            </Tooltip>
                            {
                                role === STUDENT_ROLE
                                ?
                                <div className='role'>
                                     <Tooltip title={isEllipsisActive ? StudentName || "Student" : ""} arrow>
      <div
        ref={textRef}
        className="namefield">
        {StudentName || "Student"}
      </div>
    </Tooltip>
                                    <div>{(profile?.getCurrentUser?.nodes?.[0]?.studentsByUId?.nodes?.[0]?.studentClassroomsBySId?.nodes?.[0]?.classroomByCId?.cOriginalName && profile?.getCurrentUser?.nodes?.[0]?.studentsByUId?.nodes?.[0]?.studentClassroomsBySId?.nodes?.[0]?.classroomByCId?.cOriginalName !== "") && <span>{profile?.getCurrentUser?.nodes?.[0]?.studentsByUId?.nodes?.[0]?.studentClassroomsBySId?.nodes?.[0]?.classroomByCId?.cOriginalName}</span>}</div>
                                </div>
                                : role === TEACHER_ROLE ?
                                <div className='role'>
                                    <div  className='namefield'>{`${StudentName ? StudentName : 'Teacher'}`}</div>
                                    <div>
                                        {(classRoomName && classRoomName !== "") && <span>{classRoomName}</span>}
                                    </div>
                                </div>
                                : <div className='role'>{role}</div>
                            }
                        </div>
                    </div>
                    <div className='menuItems'>
                        {getMenuItemsBySet("setOne").map((item) => {
                            const activeMenu = location.pathname.includes(item.link);
                            return (
                                <div className='menuParentContainer' key={item.id}>
                                    <div onClick={() => onMenuClick(item)} className={`menu ${activeMenu ? "menuActive" : ""}`} key={item.id}>
                                        <div className='menuIconContainer'>
                                            <img className="menuIcon" alt="sidebarIcons" src={activeMenu ? item?.activeIcon : item.inactiveIcon}/>
                                        </div>
                                        <div className="menuLabel">{item.label}</div>
                                        {item.id === "Announcement" && (notification?.Announcement ?? 0) > 0 && (
                                            <span className='notificationBadge'>{notification!.Announcement}</span>
                                        )}
                                    </div>
                                    {item.hasSubmenu && (
                                        <div className="submenu-ctn">
                                            {item.submenus && item.submenus.map((submenu) => (
                                                <div key={submenu.id} onClick={()=>handleSubmenuClick(submenu)} 
                                                className={`submenuItem ${activeTab === submenu.index ? 'submenuactiveTab' : ''}`}
                                                >
                                                    {submenu.label}
                                                    <span 
                                                    className="redDot" 
                                                    style={{ visibility: submenu.index === 0 && isLiveActive ? 'visible' : submenu.index === 1 && isMylibaryActive ? 'visible' : 'hidden' }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>
                <div  className='sidebarBottomContainer'>
                    <div className='menuItems'>
                        {getMenuItemsBySet("setTwo").map((item) => {
                            const activeMenu = location.pathname.includes(item.link);
                            return (
                                <div className='menuParentContainer'>
                                    <div onClick={() => onMenuClick(item)} className={`menu ${activeMenu ? "menuActive" : ""}`} key={item.id}>
                                        <div className='menuIconContainer'>
                                            <img className="menuIcon" alt="sidebarIcons" src={activeMenu ? item?.activeIcon : item.inactiveIcon}/>
                                        </div>
                                        <div className="menuLabel">{item.label}</div>
                                    </div>
                                </div>

                            )
                        })}
                    </div>
                    <div className='logoutContainer'>
                        {getMenuItemsBySet("setThree").map((item) => (
                            <div onClick={onLogoutClick} className='menu' key={item.id}>
                                <img className="menuIcon" alt="sidebarIcons" src={item.inactiveIcon}/>
                                <div className="menuLabel">{item.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <ConfirmationPopup
                open={openConfirmationPopup}
                message={`Are you sure you want to logout?`}
                gifData={logoutGif}
                yesButtonFunctionality={onYesButtonClick}
                noButtonFunctionality={onNoButtonClick}
                messageWidth={'166px'}
                page='logout'
            />
        </Drawer>
    )
}

export default MobileSidebar;